import type { CSSProperties } from 'react';
import { Badge, Tooltip } from '@mantine/core';
import { TasteAxisSummary, TasteAxisType } from '../../services/adminCatalogService';
import {
  FLAVOR_LABELS,
  TEXTURE_LABELS,
  FORMAT_LABELS,
  CUISINE_LABELS,
  TECHNIQUE_LABELS,
  ingredientDisplayName,
} from '../../types/tasteProfile';

/**
 * Mantine badge labels default to overflow:hidden + text-overflow:ellipsis,
 * which zeroes the badge's min-content width, so a squeezed table column
 * collapses it and ellipsizes labels like "Mediterranean". Making the label's
 * overflow visible restores the text's full width as the badge's minimum, so
 * columns can't shrink below it and the table's ScrollArea absorbs the extra
 * width instead. Pass to Badge's `styles` prop (not `style`).
 */
export const BADGE_NO_TRUNCATE: Partial<Record<'root' | 'label', CSSProperties>> = {
  root: { maxWidth: 'none' },
  label: { overflow: 'visible' },
};

export const AXIS_TYPE_LABELS: Record<TasteAxisType, string> = {
  cuisine: 'Cuisine',
  flavor: 'Flavor',
  technique: 'Technique',
  ingredient: 'Ingredient',
  format: 'Format',
  texture: 'Texture',
  dietary: 'Dietary',
};

export const AXIS_TYPE_COLORS: Record<TasteAxisType, string> = {
  cuisine: 'grape',
  flavor: 'orange',
  technique: 'blue',
  ingredient: 'green',
  format: 'cyan',
  texture: 'yellow',
  dietary: 'teal',
};

/** Human label for an axis value, using the same maps the taste profile UI uses. */
export const axisDisplayName = (type: TasteAxisType, name: string): string => {
  switch (type) {
    case 'flavor':
      return FLAVOR_LABELS[name] ?? name;
    case 'texture':
      return TEXTURE_LABELS[name] ?? name;
    case 'format':
      return FORMAT_LABELS[name] ?? name;
    case 'cuisine':
      return CUISINE_LABELS[name] ?? name;
    case 'technique':
      return TECHNIQUE_LABELS[name] ?? name;
    // Ingredient and dietary values are snake_case keys; both read well
    // through the ingredient prettifier ("gluten_free" -> "Gluten Free").
    case 'ingredient':
    case 'dietary':
      return ingredientDisplayName(name);
    default:
      return name;
  }
};

export type AxisStatus =
  | 'active'
  | 'nearFloor'
  | 'willBeDeleted'
  | 'awaitingRebuild'
  | 'insufficient';

/** The minimal slice of an axis that floor status depends on. */
export type AxisFloorInfo = Pick<
  TasteAxisSummary,
  'exists' | 'liveRecipeCount' | 'minRecipeCount'
>;

/**
 * Where this axis stands relative to its rebuild floor. Stored axes compare
 * their LIVE count against the floor because that's what the next rebuild
 * will act on; missing axes split into "cleared the floor, awaiting rebuild"
 * and "not enough recipes to exist".
 */
export const axisStatus = (a: AxisFloorInfo): AxisStatus => {
  if (a.exists) {
    if (a.liveRecipeCount < a.minRecipeCount) return 'willBeDeleted';
    if (a.liveRecipeCount < a.minRecipeCount * 1.1) return 'nearFloor';
    return 'active';
  }
  return a.liveRecipeCount >= a.minRecipeCount ? 'awaitingRebuild' : 'insufficient';
};

export const AxisStatusBadge = ({
  axis,
  size = 'sm',
}: {
  axis: AxisFloorInfo;
  size?: 'xs' | 'sm';
}) => {
  switch (axisStatus(axis)) {
    case 'active':
      return <Badge size={size} variant="light" color="green" styles={BADGE_NO_TRUNCATE}>active</Badge>;
    case 'nearFloor':
      return (
        <Tooltip label="Within 10% of the rebuild floor — at risk of deletion if recipes drop">
          <Badge size={size} variant="light" color="yellow" styles={BADGE_NO_TRUNCATE}>near floor</Badge>
        </Tooltip>
      );
    case 'willBeDeleted':
      return (
        <Tooltip label="Live recipe count is below the rebuild floor — the next rebuild will delete this axis">
          <Badge size={size} variant="light" color="red" styles={BADGE_NO_TRUNCATE}>will be deleted</Badge>
        </Tooltip>
      );
    case 'awaitingRebuild':
      return (
        <Tooltip label="Enough recipes now carry this attribute, but no embedding is stored yet — the next rebuild will create it">
          <Badge size={size} variant="light" color="blue" styles={BADGE_NO_TRUNCATE}>awaiting rebuild</Badge>
        </Tooltip>
      );
    case 'insufficient':
      return (
        <Tooltip label="No axis exists — too few catalog recipes carry this attribute to build an embedding">
          <Badge size={size} variant="light" color="gray" styles={BADGE_NO_TRUNCATE}>
            needs {axis.minRecipeCount - axis.liveRecipeCount} more
          </Badge>
        </Tooltip>
      );
  }
};
